import React from 'react';
import {
  Package,
  Warehouse,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  BarChart3,
  Eye,
  MoreHorizontal,
  Calendar,
  Truck,
  DollarSign,
  Target,
  Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import StatCard from '../../components/StatCard';

const InventoryDashboard: React.FC = () => {
  const { user } = useAuth();


  const inventoryTurnover = [
    { month: 'Jul', turnover: 8.2, daysOnHand: 44 },
    { month: 'Aug', turnover: 8.8, daysOnHand: 41 },
    { month: 'Sep', turnover: 9.1, daysOnHand: 40 },
    { month: 'Oct', turnover: 8.9, daysOnHand: 41 },
    { month: 'Nov', turnover: 9.4, daysOnHand: 39 },
    { month: 'Dec', turnover: 9.7, daysOnHand: 37 }
  ];

  const stockLevelsByCategory = [
    { category: 'Cardiovascular', current: 2450, optimal: 2200, value: 125000, color: '#8B5CF6' },
    { category: 'Endocrine', current: 1890, optimal: 1950, value: 95000, color: '#10B981' },
    { category: 'Mental Health', current: 1650, optimal: 1800, value: 75000, color: '#F59E0B' },
    { category: 'Gastrointestinal', current: 1420, optimal: 1500, value: 65000, color: '#EF4444' },
    { category: 'Respiratory', current: 1180, optimal: 1200, value: 55000, color: '#3B82F6' },
    { category: 'Other', current: 980, optimal: 1000, value: 45000, color: '#6B7280' }
  ];

  const expiryAlerts = [
    { severity: 'Expired', count: 8, value: 12500, color: '#EF4444' },
    { severity: 'Critical (≤30 days)', count: 23, value: 35000, color: '#F97316' },
    { severity: 'Warning (≤90 days)', count: 45, value: 68000, color: '#EAB308' },
    { severity: 'Normal (>90 days)', count: 1124, value: 1850000, color: '#10B981' }
  ];

  const topMovingItems = [
    { name: 'Atorvastatin 20mg', category: 'Cardiovascular', turnover: 12.5, stock: 150, reorderPoint: 50 },
    { name: 'Metformin 500mg', category: 'Endocrine', turnover: 11.8, stock: 200, reorderPoint: 75 },
    { name: 'Amlodipine 5mg', category: 'Cardiovascular', turnover: 10.9, stock: 120, reorderPoint: 40 },
    { name: 'Omeprazole 20mg', category: 'Gastrointestinal', turnover: 10.2, stock: 180, reorderPoint: 60 },
    { name: 'Sertraline 50mg', category: 'Mental Health', turnover: 9.8, stock: 90, reorderPoint: 30 }
  ];

  const lowStockAlerts = [
    {
      id: 'low-001',
      medication: 'Rosuvastatin 20mg',
      currentStock: 15,
      reorderPoint: 50,
      category: 'Cardiovascular',
      leadTime: 3,
      status: 'critical'
    },
    {
      id: 'low-002',
      medication: 'Insulin Glargine',
      currentStock: 8,
      reorderPoint: 25,
      category: 'Endocrine',
      leadTime: 5,
      status: 'critical'
    },
    {
      id: 'low-003',
      medication: 'Salbutamol Inhaler',
      currentStock: 22,
      reorderPoint: 40,
      category: 'Respiratory',
      leadTime: 2,
      status: 'low'
    },
    {
      id: 'low-004',
      medication: 'Paracetamol 500mg',
      currentStock: 85,
      reorderPoint: 100,
      category: 'Analgesic',
      leadTime: 1,
      status: 'low'
    }
  ];

  const wasteReduction = [
    { month: 'Jul', expired: 8500, returned: 3200, donated: 1500 },
    { month: 'Aug', expired: 7200, returned: 3800, donated: 2100 },
    { month: 'Sep', expired: 6800, returned: 4200, donated: 2400 },
    { month: 'Oct', expired: 5900, returned: 4500, donated: 2800 },
    { month: 'Nov', expired: 5200, returned: 4800, donated: 3200 },
    { month: 'Dec', expired: 4500, returned: 5100, donated: 3600 }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Inventory Dashboard 📦
          </h1>
          <p className="text-gray-600 mt-2">Stock optimization, turnover analytics, and inventory KPIs for {user?.branch}.</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Inventory as of</p>
          <p className="text-sm font-medium text-gray-900">{format(new Date(), 'dd MMM yyyy, HH:mm')}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Inventory Value"
          value="$2.31M"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last month"
          trendValue={3.2}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Inventory Turnover"
          value="9.7x"
          icon={<Activity className="h-6 w-6" />}
          trend="vs last month"
          trendValue={3.1}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Days on Hand"
          value="37"
          icon={<Calendar className="h-6 w-6" />}
          trend="vs target (40)"
          trendValue={-7.5}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Stock Alerts"
          value="31"
          icon={<AlertTriangle className="h-6 w-6" />}
          trend="vs last week"
          trendValue={-12.9}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Low Stock Alerts */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Low Stock Alerts</h3>
              <p className="text-gray-600 text-sm">Items requiring immediate reordering</p>
            </div>
          </div>
          <Link to="/inventory" className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1">
            <span>View All Stock</span>
            <Eye className="h-4 w-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lowStockAlerts.map((alert) => (
            <div key={alert.id} className={`p-4 rounded-xl border ${
              alert.status === 'critical' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{alert.medication}</h4>
                  <p className="text-sm text-gray-600">{alert.category}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  alert.status === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                }`}>
                  {alert.status.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-600">Stock: </span>
                  <span className="font-medium text-gray-900">{alert.currentStock} units</span>
                </div>
                <div>
                  <span className="text-gray-600">Reorder at: </span>
                  <span className="font-medium text-gray-900">{alert.reorderPoint}</span>
                </div>
                <div>
                  <span className="text-gray-600">Lead time: </span>
                  <span className="font-medium text-gray-900">{alert.leadTime} days</span>
                </div>
              </div>
              <div className="mt-3 flex space-x-2">
                <button className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700">
                  Reorder Now
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Transfer
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Inventory Turnover Trends */}
        <div className="lg:col-span-2">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Inventory Turnover Trends</h3>
                <p className="text-gray-600 text-sm">Monthly turnover rate and days on hand</p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <MoreHorizontal className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={inventoryTurnover}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Line yAxisId="left" type="monotone" dataKey="turnover" stroke="#8B5CF6" strokeWidth={3} name="Turnover Rate" />
                <Line yAxisId="right" type="monotone" dataKey="daysOnHand" stroke="#10B981" strokeWidth={3} name="Days on Hand" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expiry Status */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Expiry Status</h3>
              <p className="text-gray-600 text-sm">Medication expiry breakdown</p>
            </div>
            <Link to="/inventory/expiry" className="text-purple-600 hover:text-purple-700 text-sm font-medium">View Details</Link>
          </div>
          <div className="space-y-4">
            {expiryAlerts.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.severity}</p>
                    <p className="text-xs text-gray-500">{item.count} items</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">${(item.value / 1000).toFixed(0)}K</p>
                  <div className="w-16 bg-gray-200 rounded-full h-1 mt-1">
                    <div 
                      className="h-1 rounded-full" 
                      style={{ 
                        width: `${(item.count / 1200) * 100}%`, 
                        backgroundColor: item.color 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stock Levels by Category & Top Moving Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Stock Levels by Category */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Stock Levels by Category</h3>
              <p className="text-gray-600 text-sm">Current vs optimal stock levels</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stockLevelsByCategory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="category" axisLine={false} tickLine={false} angle={-45} textAnchor="end" height={80} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
              />
              <Bar dataKey="current" fill="#8B5CF6" name="Current Stock" radius={[4, 4, 0, 0]} />
              <Bar dataKey="optimal" fill="#E5E7EB" name="Optimal Stock" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Moving Items */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Top Moving Items</h3>
              <p className="text-gray-600 text-sm">Highest turnover medications</p>
            </div>
            <Link to="/inventory" className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</Link>
          </div>
          <div className="space-y-4">
            {topMovingItems.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl hover:bg-gray-100/50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">{item.category}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{item.turnover}x</p>
                  <p className="text-sm text-gray-500">{item.stock} units</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Waste Reduction Trends */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Waste Reduction Trends</h3>
            <p className="text-gray-600 text-sm">Monthly expired, returned, and donated medication values</p>
          </div>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <MoreHorizontal className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={wasteReduction}>
            <defs>
              <linearGradient id="colorExpired" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorReturned" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorDonated" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
              }} 
            />
            <Area type="monotone" dataKey="expired" stroke="#EF4444" fillOpacity={1} fill="url(#colorExpired)" />
            <Area type="monotone" dataKey="returned" stroke="#F59E0B" fillOpacity={1} fill="url(#colorReturned)" />
            <Area type="monotone" dataKey="donated" stroke="#10B981" fillOpacity={1} fill="url(#colorDonated)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default InventoryDashboard;
